import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Loader2, Activity, FlaskConical, Clock, PackageX, Building2, Gauge } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const TEST_LABELS = { vacuum: "Vacuum", bowie_dick: "Bowie-Dick", biological: "BI (أبواغ)" };

export default function SterilizationKPIs() {
  const { token } = useAuth();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sterilization/kpis?month=${month}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, [month, token]);

  useEffect(() => { load(); }, [load]);

  const monthOptions = (() => {
    const opts = [];
    const d = new Date();
    for (let i = 0; i < 12; i++) opts.push(new Date(d.getFullYear(), d.getMonth() - i, 15).toISOString().slice(0, 7));
    return opts;
  })();

  if (loading && !data) {
    return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#655dc6]" /></div>;
  }

  const kpiCards = [
    { label: "إجمالي الدورات", value: data?.cycles?.total ?? 0, icon: Activity, color: "bg-[#F3F2FA] text-[#655dc6]" },
    { label: "دورات فاشلة", value: data?.cycles?.failed ?? 0, icon: Gauge, color: "bg-red-50 text-red-600" },
    { label: `طلبات التعقيم — شهر ${month}`, value: data?.requests?.total ?? 0, sub: `الإجمالي منذ البداية: ${data?.requests?.all_time ?? "—"}`, icon: Building2, color: "bg-blue-50 text-blue-600" },
    { label: "متوسط زمن الدورة الكاملة", value: data?.requests?.avg_turnaround_hours != null ? `${data.requests.avg_turnaround_hours} ساعة` : "—", icon: Clock, color: "bg-amber-50 text-amber-600" },
    { label: "أدوات مفقودة", value: data?.requests?.missing_instruments ?? 0, icon: PackageX, color: "bg-rose-50 text-rose-600" },
  ];

  return (
    <div className="space-y-5" data-testid="steri-kpis-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A] tracking-tight">مؤشرات أداء قسم التعقيم</h1>
          <p className="text-gray-500 mt-1">لوحة KPIs شهرية — الدورات، الاختبارات، زمن الدورة، العيادات</p>
        </div>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="w-36" data-testid="kpi-month-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {monthOptions.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {kpiCards.map((k, i) => (
          <Card key={i} className="border border-gray-200 shadow-none rounded-xl" data-testid={`kpi-card-${i}`}>
            <CardContent className="pt-5">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${k.color}`}>
                <k.icon className="w-4.5 h-4.5 w-5 h-5" />
              </div>
              <p className="text-3xl font-light tracking-tighter text-[#0A0A0A]">{k.value}</p>
              <p className="text-xs text-gray-500 mt-1">{k.label}</p>
              {k.sub && <p className="text-[10px] text-gray-400 mt-0.5" data-testid="kpi-all-time-sub">{k.sub}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Cycles per machine */}
        <Card className="border border-gray-200 shadow-none rounded-xl" data-testid="cycles-per-machine-card">
          <CardHeader className="pb-2"><CardTitle className="text-base text-[#0A0A0A]">الدورات لكل جهاز</CardTitle></CardHeader>
          <CardContent>
            {(data?.cycles?.per_machine || []).length === 0 ? (
              <p className="text-sm text-gray-400 py-8 text-center">لا توجد دورات مسجلة لهذا الشهر</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.cycles.per_machine}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F2" />
                  <XAxis dataKey="machine" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="count" name="الدورات" fill="#655dc6" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="failed" name="فاشلة" fill="#EF4444" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Cycles per day */}
        <Card className="border border-gray-200 shadow-none rounded-xl" data-testid="cycles-per-day-card">
          <CardHeader className="pb-2"><CardTitle className="text-base text-[#0A0A0A]">الدورات حسب اليوم</CardTitle></CardHeader>
          <CardContent>
            {(data?.cycles?.per_day || []).length === 0 ? (
              <p className="text-sm text-gray-400 py-8 text-center">لا توجد دورات مسجلة لهذا الشهر</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.cycles.per_day}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F2" />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="count" name="الدورات" fill="#9b8fd4" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Tests pass rate */}
        <Card className="border border-gray-200 shadow-none rounded-xl" data-testid="tests-pass-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-[#0A0A0A] flex items-center gap-2">
              <FlaskConical className="w-4 h-4 text-[#655dc6]" /> نسبة نجاح الاختبارات
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {(data?.tests || []).length === 0 && <p className="text-sm text-gray-400 py-6 text-center">لا توجد اختبارات مسجلة لهذا الشهر</p>}
            {(data?.tests || []).map(t => (
              <div key={t.type}>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-gray-600">{TEST_LABELS[t.type] || t.type}</span>
                  <span className="font-semibold text-[#0A0A0A]">
                    {t.pass_rate}% <span className="text-gray-400 font-normal">({t.passed}/{t.total})</span>
                  </span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${t.pass_rate >= 100 ? "bg-emerald-500" : t.pass_rate >= 80 ? "bg-amber-500" : "bg-red-500"}`}
                    style={{ width: `${t.pass_rate}%` }} />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Top clinics */}
        <Card className="border border-gray-200 shadow-none rounded-xl" data-testid="top-clinics-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-[#0A0A0A] flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#655dc6]" /> أكثر العيادات طلباً للتعقيم
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(data?.requests?.top_clinics || []).length === 0 && <p className="text-sm text-gray-400 py-6 text-center">لا توجد طلبات لهذا الشهر</p>}
            {(data?.requests?.top_clinics || []).map((c, i) => (
              <div key={c.clinic} className="flex items-center justify-between border-b border-gray-50 pb-2 last:border-0">
                <div className="flex items-center gap-2">
                  <Badge className="bg-[#F3F2FA] text-[#655dc6] border-0 w-6 h-6 rounded-full flex items-center justify-center p-0">{i + 1}</Badge>
                  <span className="text-sm text-gray-700">{c.clinic}</span>
                </div>
                <span className="font-bold text-[#655dc6]">{c.count}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
